// @ts-strict-ignore
import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { View } from '@shared/components/View';
import { MBScrollView } from '@shared/components/scrollview';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2Bold } from '@shared/components/typography';
import { grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { LinkedCard } from '../cards';
import { MBInlineCardList } from '../cards/MBInlineCardList';
import { MAX_CARDS_TO_SHOW } from '../cards/constants';
import { RelatedCardsHeader } from './RelatedCardsHeader';

interface Props {
  block: { label?: string };
  cards: LinkedCard[];
  totalCardsNubmer: number;
  onEditCards(): void;
  onPressCard?(card: LinkedCard): void;
  onShowMore?(): void;
  onRemoveCard?(card: LinkedCard): void;
}

export function RelatedCardsList({
  block,
  cards,
  totalCardsNubmer,
  onShowMore,
  onEditCards,
  onPressCard,
  onRemoveCard,
}: Props) {
  const isDesktop = useIsDesktop();

  const label = useMemo(() => {
    if (isEmpty(cards) && !isNil(block.label)) {
      return block.label;
    } else if (!isEmpty(cards) && !isNil(block.label)) {
      return `${block.label} (${totalCardsNubmer})`;
    } else if (!isEmpty(cards) && isNil(block.label)) {
      return `(${totalCardsNubmer})`;
    } else {
      return '';
    }
  }, [block, totalCardsNubmer, cards]);

  const cardsToRender = useMemo(() => {
    if (!onShowMore) {
      return cards;
    } else {
      return cards?.slice(0, MAX_CARDS_TO_SHOW);
    }
  }, [cards, onShowMore]);

  const displayShowMore = onShowMore && cards?.length > MAX_CARDS_TO_SHOW;

  const scrollViewMaxHeight = useMemo(() => {
    if (!isDesktop) {
      return null;
    }
    if (displayShowMore) {
      return grid(80);
    } else {
      return grid(35);
    }
  }, [displayShowMore, isDesktop]);

  return (
    <>
      <View fullWidth paddingTop={1}>
        <RelatedCardsHeader label={label} onEditCards={onEditCards} />
        <MBScrollView style={{ maxHeight: scrollViewMaxHeight }}>
          <MBInlineCardList
            cards={cardsToRender}
            onPressCard={onPressCard}
            onRemoveCard={onRemoveCard}
          />
        </MBScrollView>
      </View>
      {displayShowMore && (
        <MBTouchableOpacity onPress={onShowMore}>
          <View centerHorizontally paddingTop={1}>
            <MBTextBody2Bold>
              {i18n('card.blocks.cardsInput.overflow', {
                count: cards.length - MAX_CARDS_TO_SHOW,
              })}
            </MBTextBody2Bold>
          </View>
        </MBTouchableOpacity>
      )}
    </>
  );
}
